import React, { useEffect } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

import { ConnectorEmptyStatePromo } from '../components/ConnectorEmptyStatePromo.tsx';
import { ConnectorNthPromoCard } from '../components/ConnectorNthPromoCard.tsx';
import { ConnectorPostConnectPromoCard } from '../components/ConnectorPostConnectPromoCard.tsx';
import { useConnectorPromoConfig } from '../hooks/useConnectorPromoConfig.ts';

interface ConnectorPromoNavSearchProps {
	hasRecentItems?: boolean;
}

function getNavSearchPromoVariant(
	shouldShowEmptyState: boolean,
	shouldShowPostConnectPromo: boolean,
	shouldShowNthPromo: boolean,
): string | null {
	switch (true) {
		case shouldShowEmptyState:
			return 'emptyState';
		case shouldShowPostConnectPromo:
			return 'postConnectPromo';
		case shouldShowNthPromo:
			return 'nthPromo';
		default:
			return null;
	}
}

/**
 * Self-contained surface component for the nav search dialog.
 * Internally calls useConnectorPromoConfig and renders the appropriate
 * promo card (empty state, Nth promo, or post-connect promo).
 */
export function ConnectorPromoNavSearch({
	hasRecentItems,
}: ConnectorPromoNavSearchProps): React.JSX.Element | null {
	const config = useConnectorPromoConfig({ hasRecentItems });
	const { createEvent } = useConfluenceAnalyticsEvents();

	const promoVariant = getNavSearchPromoVariant(
		config.shouldShowEmptyState,
		config.shouldShowPostConnectPromo,
		config.shouldShowNthPromo,
	);

	useEffect(() => {
		if (!promoVariant) {
			return;
		}
		createEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'ConnectorPromoNavSearch',
				action: 'viewed',
				actionSubject: 'connectorPromo',
				attributes: {
					promoVariant,
					container: 'ConnectorPromoNavSearch',
				},
			},
		}).fire();
	}, [promoVariant, createEvent]);

	if (config.shouldShowEmptyState) {
		return (
			<ConnectorEmptyStatePromo
				recommendedConnectors={config.recommendedConnectors}
				onSetupConnector={config.onSetupConnector}
			/>
		);
	}

	if (config.shouldShowPostConnectPromo) {
		return (
			<ConnectorPostConnectPromoCard
				connectedAppName={config.connectedAppName}
				nextRecommendedConnector={config.nextRecommendedConnector}
				onSetupConnector={config.onSetupConnector}
				onDismissPromo={() => config.onDismissPromo('banner')}
			/>
		);
	}

	if (config.shouldShowNthPromo) {
		return (
			<ConnectorNthPromoCard
				recommendedConnectors={config.recommendedConnectors}
				onDismissPromo={() => config.onDismissPromo('banner')}
			/>
		);
	}

	return null;
}
